import { createSlice } from "@reduxjs/toolkit";
import { fetchDbUser, fetchProfile } from "./auth";
import { getDashboardDetail, getHomeDashboard } from "./dashboard";
import { fetchSettings } from "./settings";

interface SharedState {
  status: "idle" | "loading" | "failed";
  menuOpen: "none" | "menu" | "alerts" | "profile";
  editMode: boolean;
  toastStatus: {
    open: "success" | "info" | "error" | "none";
    message: string;
  };
  globalLoadingState: { isLoading: boolean; loadingCount: number };
  globalReload: { isLoading: boolean; loadingTiles: number[] };
  productLicensed?: boolean;
}

const initialState: SharedState = {
  status: "idle",
  menuOpen: "none",
  editMode: false,
  toastStatus: {
    open: "none",
    message: "",
  },
  globalLoadingState: { isLoading: false, loadingCount: 0 },
  globalReload: { isLoading: false, loadingTiles: [] },
  productLicensed: undefined,
};

const sharedSlice = createSlice({
  name: "shared",
  initialState,
  reducers: {
    toggleMenu(state, action) {
      state.menuOpen = action.payload;
    },
    toggleEditMode(state, action) {
      state.editMode = action.payload;
    },
    handleToastState: (state, action) => {
      state.toastStatus = action.payload;
    },
    handleLoadingState: (state, action) => {
      const { type } = action;
      const isLoading = type.endsWith("/pending");

      if (!!isLoading) {
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: state.globalLoadingState.loadingCount + 1,
        };
      } else {
        const newCount =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: newCount > 0,
          loadingCount: newCount,
        };
      }
    },
    handleReload: (state, action) => {
      const tiles = action.payload;
      if (tiles?.length > 0) {
        state.globalReload = {
          isLoading: true,
          loadingTiles: tiles,
        };
      } else {
        state.globalReload = {
          isLoading: false,
          loadingTiles: [],
        };
      }
    },
    toggleLicense: (state, action) => {
      state.productLicensed = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(fetchProfile.rejected, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchProfile.fulfilled, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchDbUser.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(fetchDbUser.rejected, (state) => {
        state.toastStatus = {
          open: "error",
          message: "Not authorized. Please check app permissions.",
        };
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
        window.location.href = `${process.env.REACT_APP_AUTH_URL}`;
      })
      .addCase(fetchDbUser.fulfilled, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchSettings.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(fetchSettings.rejected, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchSettings.fulfilled, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(getHomeDashboard.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(getHomeDashboard.rejected, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(getHomeDashboard.fulfilled, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(getDashboardDetail.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(getDashboardDetail.rejected, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(getDashboardDetail.fulfilled, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      });
  },
});

export const {
  handleReload,
  toggleMenu,
  toggleEditMode,
  handleToastState,
  handleLoadingState,
  toggleLicense,
} = sharedSlice.actions;

export default sharedSlice.reducer;
